import type { FunctionComponent } from 'react';

import { Button, Menu, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useTranslation } from 'i18n';

import { POS_SYNC_COPY, type PosSyncScenario } from 'constants/posSyncCopy';

import { ChevronDownIconXs, PublishIconSm } from '@chownow/pantry/icons';
import { getCaretStyle, MENU_DROPDOWN_WIDTH } from './helpers';

type PublishControlProps = {
  isSingleMerchantType: boolean;
  isMobile: boolean;
  publishLocked?: boolean;
  showRefreshPOS?: boolean;
  posSyncScenario?: PosSyncScenario;
  menuCount?: number;
  importsInProgress: Record<string, string>;
  onMenuOpen: () => void;
  onPublish: () => void;
  onRefreshPOS: () => void;
};

export const PublishControl: FunctionComponent<PublishControlProps> = ({
  isSingleMerchantType,
  isMobile,
  publishLocked,
  showRefreshPOS,
  posSyncScenario = 'readOnlySync',
  menuCount,
  importsInProgress,
  onMenuOpen,
  onPublish,
  onRefreshPOS,
}) => {
  // Translations live in containers/MenuUpload/__translations__ (menuUpload namespace)
  const { t } = useTranslation('menuUpload');
  const [menuOpen, menuHandlers] = useDisclosure();
  const size = isSingleMerchantType && !isMobile ? 'md' : 'sm';

  if (isSingleMerchantType && !showRefreshPOS) {
    return (
      <Button variant="outline" size={size} disabled={publishLocked} onClick={onPublish}>
        {t('PublishControl.publish')}
      </Button>
    );
  }

  return (
    <Menu
      position="bottom-end"
      onOpen={() => {
        menuHandlers.open();
        onMenuOpen();
      }}
      onClose={menuHandlers.close}
    >
      <Menu.Target>
        <Button
          variant={isSingleMerchantType ? 'outline' : 'ghost'}
          size={size}
          leftSection={isSingleMerchantType ? undefined : <PublishIconSm />}
          rightSection={
            <span style={getCaretStyle(menuOpen)}>
              <ChevronDownIconXs />
            </span>
          }
          disabled={publishLocked}
        >
          {t('PublishControl.publish')}
        </Button>
      </Menu.Target>
      <Menu.Dropdown w={MENU_DROPDOWN_WIDTH}>
        <Menu.Item onClick={onPublish}>
          <Text variant="label-md">{t('PublishControl.publishMenus')}</Text>
        </Menu.Item>
        {showRefreshPOS && (
          <Menu.Item
            disabled={Object.values(importsInProgress).includes('pos')}
            onClick={onRefreshPOS}
          >
            <Text variant="label-md">
              {POS_SYNC_COPY[posSyncScenario].getButtonLabel(menuCount)}
            </Text>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
